import { Link } from 'react-router-dom';
import { ArrowRight, CalendarDays } from 'lucide-react';
import type { Locale } from '@/lib/i18n/config';
import type { Dictionary } from '@/lib/i18n/dictionaries';
import type { NewsPost } from '@/lib/supabase/types';
import { localeField, localePath } from '@/lib/i18n/helpers';
import { Reveal } from '@/components/site/reveal';

const fallbackCovers = [
  'https://images.pexels.com/photos/3760529/pexels-photo-3760529.jpeg?auto=compress&cs=tinysrgb&w=900',
  'https://images.pexels.com/photos/4226219/pexels-photo-4226219.jpeg?auto=compress&cs=tinysrgb&w=900',
  'https://images.pexels.com/photos/8941881/pexels-photo-8941881.jpeg?auto=compress&cs=tinysrgb&w=900',
];

export function NewsSection({
  locale,
  dict,
  news,
  compact = false,
}: {
  locale: Locale;
  dict: Dictionary;
  news: NewsPost[];
  compact?: boolean;
}) {
  return (
    <section id="noticias" className="section-pad bg-background">
      <div className="container-tight">
        {!compact && (
          <div className="mb-10 flex items-start gap-3 sm:mb-12 sm:items-center sm:gap-4">
            <span className="shrink-0 font-serif text-3xl font-bold leading-none text-accent/20 sm:text-5xl">{dict.news.sectionTitle}</span>
            <div className="min-w-0 max-w-2xl">
              <span className="eyebrow">{dict.news.title}</span>
              <h2 className="mt-1 font-serif text-2xl font-bold text-primary sm:text-3xl md:text-4xl">{dict.news.title}</h2>
              <p className="mt-2 text-base text-muted-foreground sm:text-lg">{dict.news.subtitle}</p>
            </div>
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-3">
          {news.map((n, i) => (
            <Reveal key={n.id} delay={i * 100} as="article">
              <Link
                to={localePath(`/noticias/${n.slug}`, locale)}
                className="card-surface group block h-full overflow-hidden"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                  <img
                    src={n.cover_url || fallbackCovers[i % fallbackCovers.length]}
                    alt={localeField(n, 'title', locale)}
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground">
                    <span className="rounded-full bg-accent/10 px-2.5 py-0.5 font-semibold text-accent">{n.category}</span>
                    <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{formatDate(n.published_at, locale)}</span>
                  </div>
                  <h3 className="mt-3 font-serif text-xl font-bold leading-snug text-primary transition-colors group-hover:text-accent">
                    {localeField(n, 'title', locale)}
                  </h3>
                  <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">
                    {localeField(n, 'excerpt', locale)}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-accent transition-colors">
                    {dict.news.readMore}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function formatDate(iso: string, locale: Locale) {
  const d = new Date(iso);
  return d.toLocaleDateString(locale === 'es' ? 'es-ES' : locale === 'pt' ? 'pt-BR' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
